import { cn } from '@/lib/utils';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';
import { isWeekendDay, type DayName } from '../timesheetGridUtils';

export interface GridFooterTotalsProps {
  visibleDays: readonly DayName[];
  columnTotals: number[];
  weekTotal: number;
  daysExceedingThreshold: Set<string>;
  weekExceedsThreshold: boolean;
  nonWorkingDays: Set<string>;
  organizationSettings: OrganisationTimesheetSettings | undefined;
  overtimeEnabled: boolean;
}

/** Daily total cells plus the week total cell of the grid footer row. */
export function GridFooterTotals({
  visibleDays,
  columnTotals,
  weekTotal,
  daysExceedingThreshold,
  weekExceedsThreshold,
  nonWorkingDays,
  organizationSettings,
  overtimeEnabled,
}: GridFooterTotalsProps) {
  return (
    <>
      <td
        colSpan={
          (organizationSettings?.showRole ? 1 : 0) +
          (organizationSettings?.showCostCentre !== false ? 1 : 0) +
          (overtimeEnabled ? 1 : 0) +
          1
        }
        className="p-2 sm:p-2.5 text-right text-xs sm:text-sm"
      >
        Daily Totals:
      </td>
      {columnTotals.map((total, i) => {
        const day = visibleDays[i];
        const dayBreaches = daysExceedingThreshold.has(day);
        const isNonWorking = nonWorkingDays.has(day);
        return (
          <td key={i} className={cn(
            "p-2 sm:p-2.5 text-center text-xs sm:text-sm font-medium",
            isWeekendDay(day) && 'bg-muted',
            isNonWorking && !dayBreaches && 'bg-amber-50/60 dark:bg-amber-900/20',
            dayBreaches
              ? 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300'
              : total > 0 ? 'text-foreground' : ''
          )}>
            {total.toFixed(2)}

          </td>
        );
      })}
      <td className={weekExceedsThreshold
        ? "p-2 sm:p-2.5 text-center text-sm font-bold bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300"
        : "p-2 sm:p-2.5 text-center text-sm font-bold bg-primary/10 text-primary"
      }>
        {weekTotal.toFixed(2)}
        {weekExceedsThreshold ? <div className="text-[9px] font-normal leading-none mt-0.5 opacity-75">OT threshold</div> : null}
      </td>
      <td className="p-2 sm:p-2.5"></td>
      <td aria-hidden="true" className="p-0" />
    </>
  );
}
